import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import type { GoalView, StoryLanguage } from "@/lib/games/roleplay/contract";
import { DoneTick } from "./DoneTick";

// The tutorial's goals (69; agent-service#129, spark#1075). The story's main goals stand at the top in every scene, in
// bold, each with its subgoals beneath it: the current scene's goals under „Etwas Schlaf finden“, the story's own two
// under „Die Folien für das Treffen vorbereiten“. Three levels at most, each one indented a step further. The server
// sends every goal's state (play contract §3.5), so nothing is worked out here:
// - open: a muted circle and the text;
// - reached: the platform's green tick (DoneTick, the one a met item of the readiness list wears, ReadinessList.tsx),
//   the text and „erreicht“. It stays green whatever accent the theme has; the accent is black by default;
// - crossed out, closed for good: muted, struck through, and „nicht mehr möglich“ for a screen reader.
// A scene's goals are alternatives (68): the first end reached ends the scene. So an open scene goal ends in „oder“
// while an open scene goal follows it under the same parent. Never between the story's goals, which are all needed,
// and never on the third level, where a goal stands alone beneath the one it leads to.
// A game shows none; its goals stay hidden. `fresh` marks goals that have just changed, neutrally, as everything that
// is new here: the box as a whole when a new scene brought them, or, when goals appeared within the scene (`added`,
// play contract §3.5), each of those goals by itself, on whatever level it stands (ids are unique in the tree).

const MAX_LEVEL = 3;

export function GoalList({
  goals,
  language,
  fresh = false,
  added = [],
}: {
  goals: readonly GoalView[];
  language: StoryLanguage;
  fresh?: boolean;
  added?: readonly string[];
}) {
  const marked = fresh ? added.filter((id) => contains(goals, id)) : [];
  const newMark = (
    <span className="smycles-field-meta text-ink-muted">
      <FormattedMessage id="grow.roleplay.goals.new" defaultMessage="new" />
    </span>
  );
  return (
    <section className="bg-surface box p-5">
      <BoxHeader
        title={<FormattedMessage id="grow.roleplay.goals.title" defaultMessage="Goals" />}
        right={fresh && marked.length === 0 ? newMark : null}
      />
      <Goals goals={goals} level={1} language={language} marked={marked} newMark={newMark} />
    </section>
  );
}

function contains(goals: readonly GoalView[], id: string): boolean {
  return goals.some((goal) => goal.id === id || contains(goal.subgoals, id));
}

// An open scene goal is an alternative to the open scene goals after it under the same parent.
function orFollows(siblings: readonly GoalView[], index: number, level: number): boolean {
  const isOpenScene = (goal: GoalView) => goal.state === "open" && goal.scope === "scene";
  return level < MAX_LEVEL && isOpenScene(siblings[index]!) && siblings.slice(index + 1).some(isOpenScene);
}

function Goals({
  goals,
  level,
  language,
  marked,
  newMark,
}: {
  goals: readonly GoalView[];
  level: number;
  language: StoryLanguage;
  marked: readonly string[];
  newMark: ReactNode;
}) {
  return (
    <ul className={level === 1 ? "space-y-2" : "mt-2 space-y-2 pl-6"}>
      {goals.map((goal, index) => {
        // Only a main goal of the whole game is bold: a story goal at the top. A list without main goals shows its scene
        // goals at the top in normal weight, and a story goal beneath a main goal (the slides' subgoals) is no main goal.
        const weight = level === 1 && goal.scope === "story" ? " font-semibold" : "";
        const row =
          goal.state === "reached" ? (
            <div className={`flex items-baseline gap-2 text-smycles-body${weight}`}>
              <DoneTick />
              <span className="text-ink">{goal.text[language]}</span>
              <span className="smycles-field-meta text-ink-muted">
                <FormattedMessage id="grow.roleplay.goals.reached" defaultMessage="reached" />
              </span>
              {marked.includes(goal.id) ? newMark : null}
            </div>
          ) : goal.state === "crossedOut" ? (
            <div className={`flex items-baseline gap-2 text-smycles-body text-ink-muted${weight}`}>
              <span aria-hidden="true">○</span>
              <span className="line-through">{goal.text[language]}</span>
              <span className="sr-only">
                <FormattedMessage id="grow.roleplay.goals.unreachable" defaultMessage="no longer possible" />
              </span>
            </div>
          ) : (
            <div className={`flex items-baseline gap-2 text-smycles-body${weight}`}>
              <span aria-hidden="true" className="text-ink-muted">
                ○
              </span>
              <span className="text-ink">
                {goal.text[language]}
                {orFollows(goals, index, level) ? (
                  <span className="font-normal text-ink-muted">
                    {" "}
                    <FormattedMessage id="grow.roleplay.goals.or" defaultMessage="or" />
                  </span>
                ) : null}
              </span>
              {marked.includes(goal.id) ? newMark : null}
            </div>
          );
        return (
          <li key={goal.id} data-goal-state={goal.state} data-goal-level={level}>
            {row}
            {level < MAX_LEVEL && goal.subgoals.length > 0 ? (
              <Goals goals={goal.subgoals} level={level + 1} language={language} marked={marked} newMark={newMark} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
